<template>
  <div class="tab-content catalog-management-page">
    <div class="main-header">
      <div class="page-heading">
        <h1>{{ $t('controlCenter.catalog.title') }}</h1>
        <p class="subtitle">{{ $t('controlCenter.catalog.subtitle') }}</p>
      </div>
      <button class="btn btn-primary create-catalog-btn" @click="openCreateDialog">
        <i class="fas fa-plus"></i>
        {{ $t('controlCenter.catalog.create') }}
      </button>
    </div>

    <div class="card catalog-card">
      <div class="card-header toolbar catalog-toolbar">
        <el-select v-model="activeBrand" class="brand-filter catalog-filter" @change="handleBrandChange">
          <el-option v-for="item in brandOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
        </el-select>
        <el-select v-model="activeType" class="type-filter catalog-filter" @change="handleTypeChange">
          <el-option :label="$t('controlCenter.common.all')" value="ALL"></el-option>
          <el-option
            v-for="item in typeOptions"
            :key="item.value"
            :label="$t(item.label)"
            :value="item.value"
          ></el-option>
        </el-select>
        <el-select v-model="activeCurrency" class="currency-filter catalog-filter" @change="fetchItems">
          <el-option :label="$t('controlCenter.common.all')" value="ALL"></el-option>
          <el-option
            v-for="item in currencyOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          ></el-option>
        </el-select>
        <button class="btn btn-sm refresh-btn" @click="fetchItems">
          {{ $t('controlCenter.common.refresh') }}
        </button>
      </div>
      <div class="card-body catalog-table-body">
        <el-table :data="filteredItems" v-loading="loading" style="width: 100%">
          <el-table-column prop="itemType" :label="$t('controlCenter.catalog.columns.type')" min-width="120">
            <template slot-scope="scope">
              {{ typeLabel(scope.row.itemType) }}
            </template>
          </el-table-column>
          <el-table-column prop="itemCode" :label="$t('controlCenter.catalog.columns.code')" min-width="140">
            <template slot-scope="scope">
              <span class="key-cell">{{ scope.row.itemCode }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="currency" :label="$t('controlCenter.catalog.columns.currency')" min-width="110">
            <template slot-scope="scope">
              {{ currencyLabel(scope.row.currency) }}
            </template>
          </el-table-column>
          <el-table-column prop="itemName" :label="$t('controlCenter.catalog.columns.name')" min-width="220">
            <template slot-scope="scope">
              <span class="name-cell">{{ scope.row.itemName }}</span>
            </template>
          </el-table-column>
          <el-table-column :label="$t('controlCenter.catalog.columns.oneYear')" min-width="140">
            <template slot-scope="scope">
              {{ formatAmount(scope.row.priceOneYear, scope.row.currency) }}
            </template>
          </el-table-column>
          <el-table-column :label="$t('controlCenter.catalog.columns.twoYears')" min-width="140">
            <template slot-scope="scope">
              {{ formatAmount(scope.row.priceTwoYears, scope.row.currency) }}
            </template>
          </el-table-column>
          <el-table-column prop="seatCount" :label="$t('controlCenter.catalog.columns.seatCount')" min-width="110"></el-table-column>
          <el-table-column prop="languageLimit" :label="$t('controlCenter.catalog.columns.languageLimit')" min-width="130">
            <template slot-scope="scope">
              {{ languageLimitLabel(scope.row.languageLimit) }}
            </template>
          </el-table-column>
          <el-table-column prop="sortOrder" :label="$t('controlCenter.catalog.columns.sortOrder')" min-width="110"></el-table-column>
          <el-table-column :label="$t('controlCenter.catalog.columns.enabled')" min-width="120">
            <template slot-scope="scope">
              <el-tag size="small" :type="isEnabled(scope.row) ? 'success' : 'info'">
                {{ isEnabled(scope.row) ? $t('controlCenter.catalog.enabled') : $t('controlCenter.catalog.disabled') }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column :label="$t('controlCenter.common.actions')" min-width="220">
            <template slot-scope="scope">
              <div class="action-buttons">
                <button class="btn btn-sm" @click="openEditDialog(scope.row)">
                  {{ $t('controlCenter.common.edit') }}
                </button>
                <button
                  class="btn btn-sm"
                  :class="{ 'danger-ghost': isEnabled(scope.row) }"
                  @click="toggleEnabled(scope.row)"
                >
                  {{ isEnabled(scope.row) ? $t('controlCenter.common.disable') : $t('controlCenter.common.enable') }}
                </button>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>

    <el-dialog
      :title="isEditMode ? $t('controlCenter.catalog.editTitle') : $t('controlCenter.catalog.createTitle')"
      :visible.sync="dialogVisible"
      width="620px"
    >
      <el-form label-position="top" :model="form">
        <div class="form-grid">
          <el-form-item :label="$t('controlCenter.catalog.columns.type')">
            <el-select v-model="form.itemType" style="width: 100%">
              <el-option
                v-for="item in typeOptions"
                :key="item.value"
                :label="$t(item.label)"
                :value="item.value"
              ></el-option>
            </el-select>
          </el-form-item>
          <el-form-item :label="$t('controlCenter.catalog.columns.code')">
            <el-input v-model.trim="form.itemCode"></el-input>
          </el-form-item>
          <el-form-item :label="$t('controlCenter.catalog.columns.currency')">
            <el-select v-model="form.currency" :disabled="!isMtBrand" style="width: 100%">
              <el-option
                v-for="item in availableCurrencies"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              ></el-option>
            </el-select>
          </el-form-item>
          <el-form-item class="span-2" :label="$t('controlCenter.catalog.columns.name')">
            <el-input v-model.trim="form.itemName"></el-input>
          </el-form-item>
          <el-form-item :label="$t('controlCenter.catalog.columns.oneYear')">
            <el-input-number v-model="form.priceOneYear" :min="0" :step="100" :precision="0" style="width: 100%"></el-input-number>
          </el-form-item>
          <el-form-item :label="$t('controlCenter.catalog.columns.twoYears')">
            <el-input-number v-model="form.priceTwoYears" :min="0" :step="100" :precision="0" style="width: 100%"></el-input-number>
          </el-form-item>
          <el-form-item v-if="form.itemType === 'AUDITOR_SEAT'" :label="$t('controlCenter.catalog.columns.seatCount')">
            <el-input-number v-model="form.seatCount" :min="1" :step="1" :precision="0" style="width: 100%"></el-input-number>
          </el-form-item>
          <el-form-item v-if="form.itemType === 'PLAN'" :label="$t('controlCenter.catalog.columns.languageLimit')">
            <el-input-number v-model="form.languageLimit" :min="0" :step="1" :precision="0" style="width: 100%"></el-input-number>
          </el-form-item>
          <el-form-item :label="$t('controlCenter.catalog.columns.sortOrder')">
            <el-input-number v-model="form.sortOrder" :min="0" :step="10" :precision="0" style="width: 100%"></el-input-number>
          </el-form-item>
          <el-form-item :label="$t('controlCenter.catalog.columns.enabled')">
            <el-switch v-model="form.enabled"></el-switch>
          </el-form-item>
        </div>
      </el-form>
      <span slot="footer" class="dialog-footer-actions">
        <el-button @click="dialogVisible = false">{{ $t('controlCenter.common.cancel') }}</el-button>
        <el-button type="primary" :loading="saving" @click="saveItem">
          {{ $t('controlCenter.common.save') }}
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { paymentAdminApi } from '@/api/paymentAdmin'
import brand from '@/branding'
import { displayCurrencyLabel, formatPrice } from '@/utils/currency'

const currencyForBrand = brandCode => brandCode === 'mt' ? 'EUR' : 'CNY'
const defaultForm = (brandCode = brand.id) => ({
  id: null,
  brandCode,
  itemType: 'PLAN',
  itemCode: '',
  itemName: '',
  priceOneYear: 0,
  priceTwoYears: 0,
  currency: currencyForBrand(brandCode),
  seatCount: null,
  languageLimit: null,
  sortOrder: 0,
  enabled: true
})

export default {
  name: 'CatalogManagement',
  data() {
    return {
      loading: false,
      saving: false,
      activeType: 'ALL',
      activeBrand: brand.id,
      activeCurrency: 'ALL',
      items: [],
      dialogVisible: false,
      isEditMode: false,
      form: defaultForm(brand.id),
      brandOptions: [
        { value: 'lpt', label: 'LPT' },
        { value: 'mt', label: 'MindseT' }
      ],
      typeOptions: [
        { value: 'PLAN', label: 'controlCenter.catalog.types.plan' },
        { value: 'LANGUAGE', label: 'controlCenter.catalog.types.language' },
        { value: 'STANDARD', label: 'controlCenter.catalog.types.standard' },
        { value: 'AUDITOR_SEAT', label: 'controlCenter.catalog.types.auditorSeat' },
        { value: 'ADMIN_SEAT', label: 'controlCenter.catalog.types.adminSeat' }
      ],
      currencyOptions: [
        { value: 'CNY', label: 'RMB' },
        { value: 'EUR', label: 'EUR / €' },
        { value: 'CHF', label: 'CHF / Fr.' }
      ]
    }
  },
  computed: {
    filteredItems() {
      return this.items.filter(item => {
        const typeMatched = this.activeType === 'ALL' || item.itemType === this.activeType
        const currencyMatched = this.activeCurrency === 'ALL' || (item.currency || 'CNY') === this.activeCurrency
        return typeMatched && currencyMatched
      })
    },
    isMtBrand() {
      return (this.form && this.form.brandCode) === 'mt'
    },
    availableCurrencies() {
      return this.isMtBrand
        ? [
            { value: 'EUR', label: 'EUR / €' },
            { value: 'CHF', label: 'CHF / Fr.' }
          ]
        : [{ value: 'CNY', label: 'RMB' }]
    }
  },
  created() {
    this.fetchItems()
  },
  methods: {
    async fetchItems() {
      this.loading = true
      try {
        const response = await paymentAdminApi.getCatalogItems({
          brandCode: this.activeBrand,
          currency: this.activeCurrency === 'ALL' ? undefined : this.activeCurrency
        })
        const items = response && response.data ? response.data : response
        this.items = Array.isArray(items) ? items.map(this.normalizeCatalogItem) : []
      } catch (error) {
        console.error('Failed to fetch catalog items:', error)
        this.$message.error(this.$t('controlCenter.catalog.loadError'))
      } finally {
        this.loading = false
      }
    },
    handleTypeChange() {},
    handleBrandChange() {
      this.dialogVisible = false
      this.form = defaultForm(this.activeBrand)
      this.fetchItems()
    },
    openCreateDialog() {
      this.isEditMode = false
      this.form = defaultForm(this.activeBrand)
      this.dialogVisible = true
    },
    openEditDialog(row) {
      this.isEditMode = true
      this.form = {
        id: row.id,
        brandCode: row.brandCode,
        itemType: row.itemType,
        itemCode: row.itemCode,
        itemName: row.itemName,
        currency: row.currency || 'CNY',
        priceOneYear: row.priceOneYear,
        priceTwoYears: row.priceTwoYears,
        seatCount: row.seatCount,
        languageLimit: row.languageLimit,
        sortOrder: row.sortOrder,
        enabled: this.isEnabled(row)
      }
      this.dialogVisible = true
    },
    async saveItem() {
      this.saving = true
      try {
        if (this.isEditMode) {
          await paymentAdminApi.updateCatalogItem(this.form.id, this.form)
          this.$message.success(this.$t('controlCenter.catalog.updateSuccess'))
        } else {
          await paymentAdminApi.createCatalogItem(this.form)
          this.$message.success(this.$t('controlCenter.catalog.createSuccess'))
        }
        this.dialogVisible = false
        await this.fetchItems()
      } catch (error) {
        console.error('Failed to save catalog item:', error)
        this.$message.error(error?.response?.data?.message || this.$t('controlCenter.catalog.saveError'))
      } finally {
        this.saving = false
      }
    },
    async toggleEnabled(row) {
      try {
        const currentlyEnabled = this.isEnabled(row)
        const response = currentlyEnabled
          ? await paymentAdminApi.disableCatalogItem(row.id, this.activeBrand)
          : await paymentAdminApi.enableCatalogItem(row.id, this.activeBrand)
        this.patchCatalogItem(response && response.data ? response.data : response)
        if (currentlyEnabled) {
          this.$message.success(this.$t('controlCenter.catalog.disableSuccess'))
        } else {
          this.$message.success(this.$t('controlCenter.catalog.enableSuccess'))
        }
        await this.fetchItems()
      } catch (error) {
        console.error('Failed to toggle catalog item:', error)
        this.$message.error(this.$t('controlCenter.catalog.saveError'))
      }
    },
    normalizeCatalogItem(item) {
      return {
        ...item,
        enabled: this.toBoolean(item && item.enabled)
      }
    },
    patchCatalogItem(item) {
      if (!item || item.id == null) return
      const index = this.items.findIndex(row => row.id === item.id)
      if (index < 0) return
      this.$set(this.items, index, this.normalizeCatalogItem({
        ...this.items[index],
        ...item
      }))
    },
    isEnabled(row) {
      return this.toBoolean(row && row.enabled)
    },
    toBoolean(value) {
      if (typeof value === 'boolean') return value
      if (typeof value === 'number') return value === 1
      if (typeof value === 'string') {
        return ['true', '1', 'yes', 'y'].includes(value.trim().toLowerCase())
      }
      return Boolean(value)
    },
    formatAmount(amount, currency) {
      return `${displayCurrencyLabel(currency)} ${formatPrice(Number(amount || 0) / 100)}`
    },
    currencyLabel(currency) {
      const option = this.currencyOptions.find(item => item.value === (currency || 'CNY'))
      return option ? option.label : currency || 'CNY'
    },
    typeLabel(type) {
      const option = this.typeOptions.find(item => item.value === type)
      return option ? this.$t(option.label) : type
    },
    languageLimitLabel(limit) {
      if (limit == null || Number(limit) <= 0) {
        return this.$t('controlCenter.catalog.unlimited')
      }
      return limit
    }
  }
}
</script>

<style scoped>
.catalog-management-page {
  --catalog-accent: #2563eb;
  --catalog-border: #e2e8f0;
  --catalog-muted: #64748b;
  --catalog-surface: #ffffff;
}

.catalog-management-page .main-header {
  align-items: flex-start;
  gap: 18px;
}

.page-heading {
  min-width: 0;
}

.catalog-management-page .subtitle {
  max-width: 720px;
}

.create-catalog-btn,
.refresh-btn {
  justify-content: center;
  white-space: nowrap;
}

.catalog-card {
  overflow: hidden;
  border-color: var(--catalog-border);
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.06);
}

.toolbar {
  gap: 12px;
}

.catalog-toolbar {
  justify-content: flex-start !important;
  align-items: center;
  background: #f8fafc;
}

.type-filter,
.currency-filter {
  width: 220px;
}

.catalog-filter {
  flex: 0 0 220px;
}

.refresh-btn {
  min-width: 96px;
}

.catalog-table-body {
  padding: 0 !important;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.catalog-table-body .el-table {
  min-width: 1180px;
}

.key-cell,
.name-cell {
  display: inline-block;
  max-width: 100%;
  overflow-wrap: anywhere;
}

.key-cell {
  color: #334155;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace;
  font-size: 12px;
}

.name-cell {
  color: #0f172a;
  font-weight: 600;
}

.action-buttons {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.action-buttons .btn {
  min-width: 76px;
  justify-content: center;
  white-space: nowrap;
}

.danger-ghost {
  color: #dc2626 !important;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.span-2 {
  grid-column: span 2;
}

.dialog-footer-actions {
  display: inline-flex;
  justify-content: flex-end;
  gap: 10px;
}

@media screen and (max-width: 768px) {
  .catalog-management-page .main-header {
    gap: 12px;
  }

  .catalog-management-page .page-heading,
  .catalog-management-page .create-catalog-btn {
    width: 100%;
  }

  .toolbar {
    flex-direction: column;
    align-items: stretch !important;
  }

  .type-filter,
  .currency-filter,
  .catalog-filter {
    width: 100%;
    flex-basis: auto;
  }

  .refresh-btn {
    width: 100%;
  }

  .action-buttons {
    flex-direction: column;
    align-items: stretch;
  }

  .action-buttons .btn,
  .action-buttons .el-button {
    width: 100%;
    margin-left: 0;
  }

  .catalog-table-body {
    margin: 0 -1px;
  }

  .catalog-table-body .el-table {
    min-width: 980px;
  }

  .form-grid {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .span-2 {
    grid-column: span 1;
  }

  .dialog-footer-actions {
    display: flex;
    flex-direction: column-reverse;
    gap: 8px;
    width: 100%;
  }

  .dialog-footer-actions .el-button {
    width: 100%;
    margin-left: 0;
  }
}

@media screen and (max-width: 480px) {
  .catalog-table-body .el-table {
    min-width: 900px;
  }

  .action-buttons .btn {
    min-height: 32px;
  }
}
</style>
